/**
 * Composable for editing tool implementations
 *
 * Tools only edit the map store. Nothing here touches the renderer: the
 * viewport, minimap and overlays all update from the store's tile change
 * notifications, so every edit path (tools, properties panel, undo) looks the
 * same to them.
 */

import { computed } from 'vue';
import { useMapStore } from '@/stores/map';
import { useEditorStore, EditorTool, FLAG_TOOLS, BrushTile } from '@/stores/editor';
import { TileData, TileFlagKey } from '@/formats/types';

/** Most tiles a single bucket fill will change (bounds undo memory) */
const FLOOD_FILL_LIMIT = 100000;

/** A tile the current tool would affect, relative to the cursor */
export interface ToolPreviewTile {
    offsetX: number;
    offsetY: number;
    /** Sprite that would be placed, if the tool places one */
    spriteId?: number;
    frame?: number;
    isObject?: boolean;
}

export function useTools() {
    const mapStore = useMapStore();
    const editorStore = useEditorStore();

    // Stroke state
    let strokeActive = false;
    let lastStrokeX = 0;
    let lastStrokeY = 0;
    // Value written by the current flag tool stroke
    let strokeFlagValue = true;

    // Tile brush indexed by offset, for pattern lookups
    const tilePattern = computed(() => {
        const { width, height } = editorStore.tileBrushBounds;
        const tiles = new Map<number, BrushTile>();
        for (const tile of editorStore.tileBrush) {
            tiles.set(tile.offsetY * width + tile.offsetX, tile);
        }
        return { width, height, tiles };
    });

    // The tile brush paints as a position-locked pattern rather than a stamp
    const paintsAsPattern = computed(() =>
        editorStore.alignTilePattern || editorStore.tileBrush.length <= 1
    );

    /**
     * Tile the pattern puts at a map position
     */
    function getPatternTile(x: number, y: number): { spriteId: number; frame: number } | null {
        if (editorStore.tileBrush.length === 0) {
            return { spriteId: editorStore.selectedTileSprite, frame: editorStore.selectedTileSpriteFrame };
        }

        const { width, height, tiles } = tilePattern.value;
        return tiles.get((y % height) * width + (x % width)) ?? null;
    }

    /**
     * Tile offsets covered by the brush size, centered on the cursor tile
     */
    function getSizeBrushOffsets(): Array<{ offsetX: number; offsetY: number }> {
        const size = editorStore.brushSize;
        const start = -Math.floor(size / 2);
        const offsets: Array<{ offsetX: number; offsetY: number }> = [];

        for (let dy = 0; dy < size; dy++) {
            for (let dx = 0; dx < size; dx++) {
                offsets.push({ offsetX: start + dx, offsetY: start + dy });
            }
        }

        return offsets;
    }

    function paintTiles(centerX: number, centerY: number): void {
        if (paintsAsPattern.value) {
            for (const { offsetX, offsetY } of getSizeBrushOffsets()) {
                const x = centerX + offsetX;
                const y = centerY + offsetY;
                if (x < 0 || y < 0) continue;

                const tile = getPatternTile(x, y);
                if (tile) {
                    mapStore.setBaseTile(x, y, tile.spriteId, tile.frame);
                }
            }
            return;
        }

        // Stamp the brush at the cursor
        for (const tile of editorStore.tileBrush) {
            mapStore.setBaseTile(centerX + tile.offsetX, centerY + tile.offsetY, tile.spriteId, tile.frame);
        }
    }

    function paintObjects(centerX: number, centerY: number): void {
        if (editorStore.objectBrush.length === 0) {
            mapStore.setObject(centerX, centerY, editorStore.selectedObjectSprite, editorStore.selectedObjectSpriteFrame);
            return;
        }

        for (const tile of editorStore.objectBrush) {
            mapStore.setObject(centerX + tile.offsetX, centerY + tile.offsetY, tile.spriteId, tile.frame);
        }
    }

    function eraseObjects(centerX: number, centerY: number): void {
        for (const { offsetX, offsetY } of getSizeBrushOffsets()) {
            mapStore.clearObject(centerX + offsetX, centerY + offsetY);
        }
    }

    function paintFlag(centerX: number, centerY: number, flag: TileFlagKey): void {
        const flags: Partial<Record<TileFlagKey, boolean>> = {};
        flags[flag] = strokeFlagValue;

        for (const { offsetX, offsetY } of getSizeBrushOffsets()) {
            mapStore.setTileFlags(centerX + offsetX, centerY + offsetY, flags);
        }
    }

    /**
     * Apply the current stroke tool at a position
     */
    function applyToolAt(x: number, y: number): void {
        const tool = editorStore.currentTool;

        switch (tool) {
            case EditorTool.PAINT_TILE:
                paintTiles(x, y);
                break;

            case EditorTool.PAINT_OBJECT:
                paintObjects(x, y);
                break;

            case EditorTool.ERASE_OBJECT:
                eraseObjects(x, y);
                break;

            default: {
                const flag = FLAG_TOOLS[tool];
                if (flag) {
                    paintFlag(x, y, flag);
                }
                break;
            }
        }
    }

    function clampToMap(x: number, y: number): { x: number; y: number } {
        return {
            x: Math.max(0, Math.min(mapStore.mapWidth - 1, x)),
            y: Math.max(0, Math.min(mapStore.mapHeight - 1, y)),
        };
    }

    /**
     * Left button pressed on a tile
     */
    function beginStroke(x: number, y: number): void {
        if (!mapStore.document) return;

        const tool = editorStore.currentTool;

        switch (tool) {
            case EditorTool.SELECT: {
                const start = clampToMap(x, y);
                editorStore.startSelection(start.x, start.y);
                return;
            }

            case EditorTool.EYEDROPPER:
                pickAt(x, y);
                return;

            case EditorTool.BUCKET_FILL:
                mapStore.runOperation(() => floodFill(x, y));
                return;
        }

        mapStore.beginOperation();
        strokeActive = true;
        lastStrokeX = x;
        lastStrokeY = y;

        // The first tile decides whether the whole stroke sets or clears the flag
        const flag = FLAG_TOOLS[tool];
        if (flag) {
            const startTile = mapStore.getTile(x, y);
            strokeFlagValue = startTile ? !startTile[flag] : true;
        }

        applyToolAt(x, y);
    }

    /**
     * Drag reached another tile. Pointer events skip tiles on fast drags, so
     * the tool is applied along the line from the previous tile.
     */
    function continueStroke(x: number, y: number): void {
        if (editorStore.isSelecting) {
            const end = clampToMap(x, y);
            editorStore.updateSelection(end.x, end.y);
            return;
        }

        if (!strokeActive) return;

        // Bresenham from the last applied tile (exclusive) to this one
        let cx = lastStrokeX;
        let cy = lastStrokeY;
        const dx = Math.abs(x - cx);
        const dy = -Math.abs(y - cy);
        const stepX = cx < x ? 1 : -1;
        const stepY = cy < y ? 1 : -1;
        let err = dx + dy;

        while (cx !== x || cy !== y) {
            const e2 = 2 * err;
            if (e2 >= dy) {
                err += dy;
                cx += stepX;
            }
            if (e2 <= dx) {
                err += dx;
                cy += stepY;
            }
            applyToolAt(cx, cy);
        }

        lastStrokeX = x;
        lastStrokeY = y;
    }

    /**
     * Left button released
     */
    function endStroke(): void {
        if (editorStore.isSelecting) {
            editorStore.endSelection();
        }

        if (strokeActive) {
            strokeActive = false;
            mapStore.endOperation();
        }
    }

    /**
     * Eyedropper: pick the object under the cursor, or the tile if there is none
     */
    function pickAt(x: number, y: number): void {
        const tile = mapStore.getTile(x, y);
        if (!tile) return;

        if (tile.objectSprite > 0) {
            editorStore.selectObject(tile.objectSprite, tile.objectSpriteFrame);
            editorStore.setTool(EditorTool.PAINT_OBJECT);
        } else {
            editorStore.selectTile(tile.tileSprite, tile.tileSpriteFrame);
            editorStore.setTool(EditorTool.PAINT_TILE);
        }
    }

    function isInSelection(x: number, y: number): boolean {
        const bounds = editorStore.selectionBounds;
        return bounds !== null &&
            x >= bounds.left && x <= bounds.right &&
            y >= bounds.top && y <= bounds.bottom;
    }

    function fillTile(x: number, y: number): void {
        const tile = getPatternTile(x, y);
        if (tile) {
            mapStore.setBaseTile(x, y, tile.spriteId, tile.frame);
        }
    }

    /**
     * Bucket fill. Inside a selection it fills the selection; otherwise it
     * fills the connected area sharing the clicked tile's sprite. Matching is
     * by sprite, not frame: terrain is a multi-frame pattern, so a frame match
     * would only ever fill one tile of it.
     */
    function floodFill(startX: number, startY: number): void {
        const doc = mapStore.document;
        const startTile = mapStore.getTile(startX, startY);
        if (!doc || !startTile) return;

        if (isInSelection(startX, startY)) {
            const { left, top, right, bottom } = editorStore.selectionBounds!;
            for (let x = left; x <= right; x++) {
                for (let y = top; y <= bottom; y++) {
                    fillTile(x, y);
                }
            }
            return;
        }

        const { width, height } = doc;
        const targetSprite = startTile.tileSprite;

        const visited = new Uint8Array(width * height);
        const stack: number[] = [startY * width + startX];
        visited[stack[0]] = 1;
        let filled = 0;

        while (stack.length > 0) {
            const index = stack.pop()!;
            const x = index % width;
            const y = (index - x) / width;

            fillTile(x, y);

            if (++filled >= FLOOD_FILL_LIMIT) {
                mapStore.lastError = `Fill stopped after ${FLOOD_FILL_LIMIT.toLocaleString()} tiles. Click again to continue.`;
                break;
            }

            const neighbors = [
                x > 0 ? index - 1 : -1,
                x < width - 1 ? index + 1 : -1,
                y > 0 ? index - width : -1,
                y < height - 1 ? index + width : -1,
            ];

            for (const next of neighbors) {
                if (next < 0 || visited[next]) continue;
                visited[next] = 1;

                const nx = next % width;
                const ny = (next - nx) / width;
                if (doc.tiles[nx][ny].tileSprite === targetSprite) {
                    stack.push(next);
                }
            }
        }
    }

    /**
     * Copy selection to the clipboard
     */
    function copySelection(): void {
        if (!editorStore.selectionBounds || !mapStore.document) return;

        const { left, top, width, height } = editorStore.selectionBounds;
        const copied: TileData[][] = [];

        for (let x = 0; x < width; x++) {
            copied[x] = [];
            for (let y = 0; y < height; y++) {
                const tile = mapStore.getTile(left + x, top + y);
                if (tile) {
                    copied[x][y] = { ...tile };
                }
            }
        }

        editorStore.clipboard = copied;
    }

    /**
     * Paste the clipboard with its top-left at a position
     */
    function pasteAt(startX: number, startY: number): void {
        const tiles = editorStore.clipboard;
        if (!mapStore.document || !tiles || tiles.length === 0) return;

        mapStore.runOperation(() => {
            for (let x = 0; x < tiles.length; x++) {
                for (let y = 0; y < tiles[x].length; y++) {
                    // reserved is the destination tile's own junk byte, not content
                    const { reserved: _reserved, ...content } = tiles[x][y];
                    mapStore.setTile(startX + x, startY + y, content);
                }
            }
        });

        // Select what was pasted so it can be seen and pasted over again
        const end = clampToMap(startX + tiles.length - 1, startY + tiles[0].length - 1);
        const start = clampToMap(startX, startY);
        editorStore.selection = { x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    }

    /**
     * Paste at the hovered tile, or over the selection if the cursor is off the map
     */
    function paste(): void {
        if (editorStore.tileX >= 0 && editorStore.tileY >= 0) {
            pasteAt(editorStore.tileX, editorStore.tileY);
        } else if (editorStore.selectionBounds) {
            pasteAt(editorStore.selectionBounds.left, editorStore.selectionBounds.top);
        }
    }

    /**
     * Apply a change to every tile in the selection as one undo step
     */
    function editSelection(tile: Partial<TileData>): void {
        if (!editorStore.selectionBounds || !mapStore.document) return;

        const { left, top, right, bottom } = editorStore.selectionBounds;

        mapStore.runOperation(() => {
            for (let x = left; x <= right; x++) {
                for (let y = top; y <= bottom; y++) {
                    mapStore.setTile(x, y, tile);
                }
            }
        });
    }

    /**
     * Clear objects in selection
     */
    function clearSelectionObjects(): void {
        editSelection({ objectSprite: 0, objectSpriteFrame: 0 });
    }

    /**
     * Tiles the current tool would affect around a cursor position
     */
    function getPreviewTiles(centerX: number, centerY: number): ToolPreviewTile[] {
        const tool = editorStore.currentTool;

        if (tool === EditorTool.PAINT_TILE) {
            if (!paintsAsPattern.value) {
                return editorStore.tileBrush.map(t => ({
                    offsetX: t.offsetX,
                    offsetY: t.offsetY,
                    spriteId: t.spriteId,
                    frame: t.frame,
                }));
            }

            return getSizeBrushOffsets().map(({ offsetX, offsetY }) => {
                const x = centerX + offsetX;
                const y = centerY + offsetY;
                const tile = x >= 0 && y >= 0 ? getPatternTile(x, y) : null;
                return { offsetX, offsetY, spriteId: tile?.spriteId, frame: tile?.frame };
            });
        }

        if (tool === EditorTool.PAINT_OBJECT) {
            if (editorStore.objectBrush.length === 0) {
                return [{
                    offsetX: 0,
                    offsetY: 0,
                    spriteId: editorStore.selectedObjectSprite,
                    frame: editorStore.selectedObjectSpriteFrame,
                    isObject: true,
                }];
            }

            return editorStore.objectBrush.map(t => ({
                offsetX: t.offsetX,
                offsetY: t.offsetY,
                spriteId: t.spriteId,
                frame: t.frame,
                isObject: true,
            }));
        }

        if (tool === EditorTool.ERASE_OBJECT || FLAG_TOOLS[tool]) {
            return getSizeBrushOffsets();
        }

        return [{ offsetX: 0, offsetY: 0 }];
    }

    return {
        beginStroke,
        continueStroke,
        endStroke,
        pickAt,
        copySelection,
        paste,
        editSelection,
        clearSelectionObjects,
        getPreviewTiles,
    };
}
